import { useEffect, useRef, useState } from 'react'
import { Autocomplete } from '@react-google-maps/api'
import { MapPin } from 'lucide-react'
import api from '@/lib/axiosInstance'
import { useGoogleMaps } from '@/components/GoogleMapsProvider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { States } from '@/types/States'
import type { Cities } from '@/types/Cities'
import { useAgentSettings } from '@/lib/agentSettings'
import { useAIIntake, type IntakeDraft, type IntakeFields } from './aiIntake'
import { FieldRow, SectionTitle, TextField } from './ReviewBits'
import { inputClass } from './reviewStyles'
import { isEmpty as rowEmpty, problemWith, type Row } from './reviewFields'
import { awaitingLot, lookUpLot, withLookup, withoutLookup } from './myLotLookup'
import LocationMap from './LocationMap'

/**
 * The listing's location, filled the way the form fills it: search the
 * address on Google, or pick the state and then one of its cities, and
 * click the map to say where it is. When Google does not know the place the
 * agent switches to typing the address by hand, as the form allows. A title
 * rarely gives a postcode or street, so when the dialog first shows a draft
 * with gaps, Google is asked once with whatever the files did say (the lot,
 * mukim, district, city) and fills only what is still empty. Nothing the AI
 * read or the agent typed is overwritten by that lookup.
 */

// The same lists the form loads, fetched once per page.
let statesLoad: Promise<States[]> | null = null
const loadStates = () =>
    (statesLoad ??= api
        .get('/listings/criterias')
        .then((r) => (r.data?.states ?? []) as States[])
        .catch(() => {
            statesLoad = null
            return []
        }))
const citiesLoad = new Map<number, Promise<Cities[]>>()
const loadCities = (stateId: number) => {
    if (!citiesLoad.has(stateId)) {
        citiesLoad.set(
            stateId,
            api
                .get(`/listings/states/${stateId}/cities`)
                .then((r) => (r.data?.cities ?? []) as Cities[])
                .catch(() => {
                    citiesLoad.delete(stateId)
                    return []
                }),
        )
    }
    return citiesLoad.get(stateId)!
}

// Google answers in Malay for a state the list names in English.
const norm = (s: unknown) => String(s ?? '').trim().toLowerCase().replace(/^pulau pinang$/, 'penang')
/** Google says "Wilayah Persekutuan Kuala Lumpur", the list says "Kuala Lumpur": either containing the other is a match. */
const sameName = (a: unknown, b: unknown) => {
    const x = norm(a)
    const y = norm(b)
    return Boolean(x && y && (x === y || x.includes(y) || y.includes(x)))
}

interface Place {
    formatted?: string
    street?: string
    township?: string
    city?: string
    state?: string
    postcode?: string
    /** Where the form will drop its pin. */
    lat?: number
    lng?: number
}

/** What the form defaults an approximate-location circle to, in metres. */
export const DEFAULT_RADIUS = 200

type Component = { long_name: string; types: string[] }

/** The same mapping the form uses for a Google result. */
const fromComponents = (components: Component[] = [], formatted?: string): Place => {
    const place: Place = { formatted }
    for (const c of components) {
        if (c.types.includes('route')) place.street = c.long_name
        if (c.types.includes('sublocality') || c.types.includes('sublocality_level_1')) place.township = c.long_name
        if (c.types.includes('locality')) place.city = c.long_name
        if (c.types.includes('administrative_area_level_1')) place.state = c.long_name
        if (c.types.includes('postal_code')) place.postcode = c.long_name
    }
    return place
}

const geocode = async (address: string): Promise<{ place: Place; partial: boolean } | null> => {
    const key = import.meta.env.VITE_GOOGLE_MAPS_KEY
    if (!key || !address) return null
    try {
        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&components=country:MY&key=${key}`)
        const data = await res.json()
        const first = data?.status === 'OK' ? data.results?.[0] : null
        if (!first) return null
        const place = fromComponents(first.address_components, first.formatted_address)
        const point = first.geometry?.location
        if (point) {
            place.lat = Number(point.lat)
            place.lng = Number(point.lng)
        }
        return { place, partial: Boolean(first.partial_match) }
    } catch {
        return null
    }
}

/**
 * What Google knows about a point: the road, township, city, state and
 * postcode it sits in. Land found on JUPEM MyLot arrives as a point with no
 * address, and this is what fills the address in around it.
 */
const placeAt = async (lat: number, lng: number): Promise<Place | null> => {
    const key = import.meta.env.VITE_GOOGLE_MAPS_KEY
    if (!key) return null
    try {
        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${key}`)
        const data = await res.json()
        if (data?.status !== 'OK') return null
        // The nearest result first; each later, wider one only fills what is still missing.
        const place: Place = {}
        for (const result of data.results ?? []) {
            const found = fromComponents(result.address_components)
            for (const k of ['street', 'township', 'city', 'state', 'postcode'] as const) if (!place[k] && found[k]) place[k] = found[k]
        }
        return place
    } catch {
        return null
    }
}

/**
 * The postcode for a point. A search for a lot or a road often comes back
 * without one, but the point it returns sits inside a postcode area, and
 * asking Google what is at that point does give it.
 */
const postcodeAt = async (lat: number, lng: number): Promise<string | null> => {
    const key = import.meta.env.VITE_GOOGLE_MAPS_KEY
    if (!key) return null
    try {
        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${key}`)
        const data = await res.json()
        if (data?.status !== 'OK') return null
        for (const result of data.results ?? []) {
            const found = fromComponents(result.address_components).postcode
            if (found) return found
        }
        return null
    } catch {
        return null
    }
}

const isEmpty = (v: unknown) => v === null || v === undefined || v === ''

/** A place turned into draft fields, with the state and city resolved to the form's own IDs. */
const toFields = async (place: Place): Promise<IntakeFields> => {
    const out: IntakeFields = {}
    if (place.formatted) out.rawAddress = place.formatted
    if (typeof place.lat === 'number' && typeof place.lng === 'number') {
        out.lat = place.lat
        out.lng = place.lng
        // The form asks for a postcode the documents rarely carry; the point
        // Google just gave is inside one, so ask what is there.
        if (!place.postcode) {
            const found = await postcodeAt(place.lat, place.lng)
            if (found) out.postcode = found
        }
    }
    if (place.street) out.street = place.street
    if (place.township) out.township = place.township
    if (place.postcode) out.postcode = place.postcode
    const states = await loadStates()
    const state = place.state ? states.find((s) => sameName(s.name, place.state)) : undefined
    if (state) {
        out.state = state.name
        out.stateId = state.id
        const cities = await loadCities(state.id)
        const city = place.city ? cities.find((c) => sameName(c.name, place.city)) ?? cities.find((c) => sameName(c.name, place.township)) : undefined
        if (city) {
            out.city = city.name
            out.cityId = city.id
        }
    }
    return out
}

/** Lookups under way, so a draft shown twice is still looked up once. */
const lotLookups = new Set<string>()

export default function LocationFields({ index, disabled, rows }: { index: number; disabled: boolean; rows: Row[] }) {
    const { drafts, updateDraft } = useAIIntake()
    const { settings } = useAgentSettings()
    const { isLoaded } = useGoogleMaps()
    const draft = drafts[index]
    const f = draft.fields
    const [states, setStates] = useState<States[]>([])
    const [cities, setCities] = useState<Cities[]>([])
    const [looking, setLooking] = useState(false)
    const autocomplete = useRef<google.maps.places.Autocomplete | null>(null)

    const stateId = typeof f.stateId === 'number' ? f.stateId : states.find((s) => sameName(s.name, f.state))?.id ?? null
    const cityId = typeof f.cityId === 'number' ? f.cityId : cities.find((c) => sameName(c.name, f.city))?.id ?? null

    useEffect(() => {
        loadStates().then(setStates)
    }, [])
    useEffect(() => {
        if (stateId) loadCities(stateId).then(setCities)
        else setCities([])
    }, [stateId])

    const apply = (fields: IntakeFields, source: 'google' | 'agent', onlyEmpty = false) =>
        updateDraft(index, (d: IntakeDraft) => {
            const patch = onlyEmpty ? Object.fromEntries(Object.entries(fields).filter(([k]) => isEmpty(d.fields[k]))) : fields
            const filled = Object.keys(patch).filter((k) => !k.endsWith('Id'))
            return {
                ...d,
                fields: { ...d.fields, ...patch },
                missingFields: d.missingFields.filter((k) => !filled.includes(k)),
                geocoded: d.geocoded || source === 'google',
                googleFilled: source === 'google' ? [...new Set([...(d.googleFilled ?? []), ...filled])] : (d.googleFilled ?? []).filter((k) => !filled.includes(k)),
            }
        })

    // A state or city the AI or Google named that matches one of the form's
    // rows is that row: keep its ID on the draft. Showing it as picked without
    // saving the ID is what let a listing be submitted with the city filled in
    // on screen and missing in what was sent.
    useEffect(() => {
        const settle: IntakeFields = {}
        if (stateId && typeof f.stateId !== 'number') settle.stateId = stateId
        if (cityId && typeof f.cityId !== 'number') settle.cityId = cityId
        if (!Object.keys(settle).length) return
        updateDraft(index, (d: IntakeDraft) => ({ ...d, fields: { ...d.fields, ...settle } }))
    }, [stateId, cityId, f.stateId, f.cityId, index, updateDraft])

    // Land the API could not look up on JUPEM MyLot is looked up from here.
    const lotPending = awaitingLot(draft)
    useEffect(() => {
        if (disabled || !lotPending) return
        const key = `${index}|${draft.lotLookup?.key ?? ''}`
        if (lotLookups.has(key)) return
        lotLookups.add(key)
        lookUpLot(draft.fields)
            .then((found) => updateDraft(index, (d: IntakeDraft) => withLookup(d, found.lotLookup, found.fields)))
            .catch((error) => updateDraft(index, (d: IntakeDraft) => withoutLookup(d, error)))
            .finally(() => lotLookups.delete(key))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, lotPending, disabled])

    // Once per draft: ask Google with what the files did say, and fill the gaps.
    // It waits for the MyLot lookup, whose pin is the better one to ask about.
    useEffect(() => {
        if (disabled || draft.geocoded || lotPending) return
        const gaps = ['rawAddress', 'postcode', 'city', 'state', 'township', 'street', 'lat', 'radius'].some((k) => isEmpty(f[k]))
        // Each part once: an address made up from the mukim and district already says them.
        const said = String(f.rawAddress ?? '').toLowerCase()
        const query = [f.rawAddress, ...[f.street, f.township, f.mukim, f.district, f.city, f.state].filter((x) => !isEmpty(x) && !said.includes(String(x).toLowerCase()))].filter((x) => !isEmpty(x)).join(', ')
        if (!gaps || !query) return
        let cancelled = false
        setLooking(true)
        // Land the API found on JUPEM MyLot already has its pin, and a better
        // one than a search for the mukim's name would give: ask Google what
        // is at that point instead of where the words are.
        const pinned = typeof f.lat === 'number' && typeof f.lng === 'number' ? { lat: f.lat, lng: f.lng } : null
        const asked: Promise<{ place: Place; partial: boolean } | null> = pinned ? placeAt(pinned.lat, pinned.lng).then((place) => (place ? { place, partial: false } : null)) : geocode(`${query}, Malaysia`)
        asked
            .then(async (hit) => {
                if (cancelled) return
                if (!hit) return apply({ radius: settings.defaultRadius || DEFAULT_RADIUS }, 'google', true)
                // A partial match only knows the area, so it may not name a
                // street; its point and postcode still place the listing.
                // The address line is only taken from an exact match, and only
                // lands when the documents and the API gave none (see onlyEmpty).
                const place = hit.partial ? { state: hit.place.state, city: hit.place.city, lat: hit.place.lat, lng: hit.place.lng } : hit.place
                apply({ ...(await toFields(place)), radius: settings.defaultRadius || DEFAULT_RADIUS }, 'google', true)
            })
            .finally(() => !cancelled && setLooking(false))
        return () => {
            cancelled = true
        }
        // Keyed on the draft: runs when a different listing is shown.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, draft.geocoded, lotPending])

    // Typing the address by hand, for a place Google does not have. Kept on
    // the draft so the listing is created the way the form creates one.
    const manual = f.manualAddress === 1
    const setManual = (on: boolean) => updateDraft(index, (d: IntakeDraft) => ({ ...d, fields: { ...d.fields, manualAddress: on ? 1 : null } }))

    // A pin placed on the map. Searching with Google, the parts of the
    // address around the new point follow it; typing by hand, only the pin
    // moves and what the agent typed stays as typed.
    const movePin = async (lat: number, lng: number) => {
        apply({ lat, lng }, 'agent')
        if (manual) return
        setLooking(true)
        try {
            const place = await placeAt(lat, lng)
            if (!place) return
            // The address line and the pin are the agent's; the rest follows the point.
            const around = await toFields({ ...place, lat, lng })
            const parts: IntakeFields = Object.fromEntries(Object.entries(around).filter(([k]) => !['rawAddress', 'lat', 'lng'].includes(k)))
            // A pin moved into another state leaves the old city behind unless Google named the new one.
            if (typeof parts.stateId === 'number' && parts.stateId !== stateId && typeof parts.cityId !== 'number') Object.assign(parts, { city: null, cityId: null })
            apply(parts, 'google')
        } finally {
            setLooking(false)
        }
    }

    const fromGoogle = (key: string) => (draft.googleFilled ?? []).includes(key)
    const text = (row: Row) => (
        <FieldRow key={row.key} label={row.label} required={row.required}>
            <TextField
                value={rowEmpty(f[row.key]) ? '' : String(f[row.key])}
                required={row.required}
                numeric={row.kind === 'number'}
                suffix={row.suffix}
                problem={problemWith(draft, row)}
                disabled={disabled}
                onCommit={(raw) => apply({ [row.key]: row.kind === 'number' ? (raw.trim() ? Number(raw.replace(/[^\d.]/g, '')) : null) : raw.trim() || null }, 'agent')}
            />
            {fromGoogle(row.key) && <span className='mt-0.5 block text-[11px] text-muted-foreground'>From Google</span>}
        </FieldRow>
    )

    const pickState = async (id: number) => {
        const state = states.find((s) => s.id === id)
        if (!state) return
        const list = await loadCities(id)
        const keepCity = list.some((c) => c.id === cityId)
        apply({ state: state.name, stateId: id, ...(keepCity ? {} : { city: null, cityId: null }) }, 'agent')
    }
    const pickCity = (id: number) => {
        const city = cities.find((c) => c.id === id)
        if (city) apply({ city: city.name, cityId: id }, 'agent')
    }

    return (
        <section>
            <SectionTitle
                aside={
                    <span className='flex items-center gap-3 text-[11px] text-muted-foreground'>
                        {looking && <span>Checking Google…</span>}
                        {!disabled && (
                            <span>
                                {manual ? '' : 'Can’t find the address? '}
                                <button type='button' onClick={() => setManual(!manual)} className='cursor-pointer font-medium text-gray-700 underline-offset-2 hover:underline'>
                                    {manual ? 'Search with Google' : 'Enter manually'}
                                </button>
                            </span>
                        )}
                    </span>
                }>
                Location
            </SectionTitle>

            {isLoaded && !disabled && !manual && (
                <Autocomplete
                    onLoad={(a) => (autocomplete.current = a)}
                    options={{ componentRestrictions: { country: 'my' }, fields: ['address_components', 'formatted_address', 'geometry'] }}
                    onPlaceChanged={async () => {
                        const place = autocomplete.current?.getPlace()
                        if (!place?.address_components) return
                        const found = fromComponents(place.address_components as Component[], place.formatted_address)
                        // The place's own point, so the pin moves to what was picked.
                        const at = place.geometry?.location
                        if (at) Object.assign(found, { lat: at.lat(), lng: at.lng() })
                        apply(await toFields(found), 'agent')
                    }}>
                    <div className='relative my-2'>
                        <MapPin className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                        <input
                            placeholder='Search the address on Google to fill these in'
                            className='h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-3 text-sm outline-none placeholder:text-muted-foreground focus:border-gray-400'
                        />
                    </div>
                </Autocomplete>
            )}

            {isLoaded && (
                <>
                    <LocationMap
                        lat={typeof f.lat === 'number' ? f.lat : null}
                        lng={typeof f.lng === 'number' ? f.lng : null}
                        radius={typeof f.radius === 'number' ? f.radius : null}
                        disabled={disabled}
                        onMove={movePin}
                        onRadius={(radius) => apply({ radius }, 'agent')}
                    />
                    {!disabled && (
                        <p className='mb-2 text-[11px] text-muted-foreground'>
                            {manual
                                ? 'Type the address below, then click the map to place the pin. Drag the edge of the circle to set the area shown.'
                                : 'Click the map or drag the pin to move the location. Drag the edge of the circle to set the area shown.'}
                        </p>
                    )}
                </>
            )}

            <dl>
                {rows.map((row) =>
                    row.key === 'state' ? (
                        <FieldRow key='state' label='State' required>
                            <Select disabled={disabled || !states.length} value={stateId ? String(stateId) : undefined} onValueChange={(v) => pickState(Number(v))}>
                                <SelectTrigger size='sm' className={`w-full ${inputClass(!stateId, true)} h-auto`}>
                                    <SelectValue placeholder='Required' />
                                </SelectTrigger>
                                <SelectContent>
                                    {states.map((s) => (
                                        <SelectItem key={s.id} value={String(s.id)}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>
                    ) : row.key === 'city' ? (
                        <FieldRow key='city' label='City' required>
                            <Select disabled={disabled || !stateId || !cities.length} value={cityId ? String(cityId) : undefined} onValueChange={(v) => pickCity(Number(v))}>
                                <SelectTrigger size='sm' className={`w-full ${inputClass(!cityId, true)} h-auto`}>
                                    <SelectValue placeholder={stateId ? 'Required' : 'Pick the state first'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {cities.map((c) => (
                                        <SelectItem key={c.id} value={String(c.id)}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>
                    ) : (
                        text(row)
                    ),
                )}
            </dl>
        </section>
    )
}
